import { useCallback, useMemo, useState, type FC } from 'react';
import { LayoutList, Table2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';

import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import FormVersioningBanner from 'components/IAP/femaCards/FormVersioningBanner';
import { useRecordFormVersionSave } from 'components/IAP/hooks/icsFormVersionHooks';
import { useIncidentInfo } from 'components/IAP/hooks/incidentInfoHooks';
import { useICSContext } from 'components/IAP/planningView/ICSContext';
import { useUscgMapContext } from 'components/MapPage/CorporateViewTabs/LayersTab/UscgMapContext';
import {
  useCreateIncidentObjective,
  useEditIncidentObjective,
  useIncidentObjectives,
  type IncidentObjective,
} from 'components/IAP/hooks/incidentObjectiveHooks';
import {
  useCreateIncidentStrategy,
  useEditIncidentStrategy,
  useIncidentStrategies,
  type IncidentStrategy,
} from 'components/IAP/hooks/incidentStrategyHooks';
import {
  useCreateIncidentTactic,
  useEditIncidentTactic,
  useIncidentTactics,
  type IncidentTactic,
} from 'components/IAP/hooks/incidentTacticHooks';
import {
  useIncident,
  useIncidentPermissions,
} from 'components/IAP/hooks/incidentHooks';
import { OperationalPeriodScheduleControl } from '../OperationalPeriodScheduleControl';
import { IapScope } from 'components/IAP/ICSConstants';
import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { BoxInfo, PrintedValue } from '../icsBoxPrimitives';
import {
  ICS234_BOX_LABELS,
  ICS234_INCIDENT_LOCATION_HINT,
  ICS234_INCIDENT_NAME_HINT,
  ICS234_MATRIX_HINT,
  ICS234_OPERATIONAL_PERIOD_HINT,
} from './constants';
import Ics234SignatureBox from './Ics234SignatureBox';
import { Ics234WorkAnalysisMatrixList } from './Ics234WorkAnalysisMatrixList';
import { Ics234WorkAnalysisMatrixTable } from './Ics234WorkAnalysisMatrixTable';
import { MATRIX_OUTLINE_BTN } from './matrixStyles';
import type {
  Ics234MatrixItemDraft,
  Ics234MatrixItemEditState,
  Ics234MatrixItemRef,
  Ics234ObjectiveRow,
} from './types';

type MatrixViewMode = 'list' | 'table';

function tacticDisplayName(tactic: IncidentTactic): string {
  return (tactic.description || tactic.name || '').trim();
}

const byNewest = (
  a: { timestamp: Date | string },
  b: { timestamp: Date | string }
) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime();

/**
 * Work Analysis Matrix, the ICS 234-CG.
 *
 * Boxes 1-3 are the read-only header. Boxes 4-6 are the three columns of one
 * matrix rather than three stacked boxes, so they share a section. Box 7 is the
 * Planning Section Chief's signature.
 */
const Ics234StandaloneMatrix: FC = () => {
  const scope = 'incident' as IapScope;
  const { data: incidentObjectives } = useIncidentObjectives(scope);
  const { data: incidentStrategies } = useIncidentStrategies(scope);
  const { data: incidentTactics } = useIncidentTactics(scope);
  const { data: incidentPermissions } = useIncidentPermissions();
  const canEdit = incidentPermissions?.['incident:editCards'] ?? false;

  const { effectivePeriodNumber, isFuture, readOnlyForms } = useICSContext();
  const { isMapVisible } = useUscgMapContext();
  const incident = useIncident();
  const { data: incidentInfo } = useIncidentInfo();

  /**
   * The 234 is a versioned form, so each write should leave a draft version
   * behind. Recorded after the write lands, so the server snapshots values that
   * are already persisted.
   */
  const recordVersionSave = useRecordFormVersionSave(
    'ICS_234',
    effectivePeriodNumber
  );
  const recordVersion = useCallback(() => {
    if (!readOnlyForms) recordVersionSave.mutate();
  }, [readOnlyForms, recordVersionSave]);

  const createObjective = useCreateIncidentObjective();
  const editObjective = useEditIncidentObjective();
  const createStrategy = useCreateIncidentStrategy();
  const editStrategy = useEditIncidentStrategy();
  const createTactic = useCreateIncidentTactic();
  const editTactic = useEditIncidentTactic();
  const { exportAsForm } = useIcsFormPdfPreview();

  const [editingItem, setEditingItem] =
    useState<Ics234MatrixItemEditState | null>(null);
  const [matrixViewMode, setMatrixViewMode] = useState<MatrixViewMode>('table');

  const isSaving =
    editObjective.isPending ||
    editStrategy.isPending ||
    editTactic.isPending ||
    createObjective.isPending ||
    createStrategy.isPending ||
    createTactic.isPending;

  const objectivesById = useMemo(() => {
    const map = new Map<string, IncidentObjective>();
    incidentObjectives?.forEach((obj) => map.set(obj.objective_id, obj));
    return map;
  }, [incidentObjectives]);

  const strategiesById = useMemo(() => {
    const map = new Map<string, IncidentStrategy>();
    incidentStrategies?.forEach((strat) => map.set(strat.strategy_id, strat));
    return map;
  }, [incidentStrategies]);

  const tacticsById = useMemo(() => {
    const map = new Map<string, IncidentTactic>();
    incidentTactics?.forEach((tac) => map.set(tac.tactic_id, tac));
    return map;
  }, [incidentTactics]);

  const nestedObjectives: Ics234ObjectiveRow[] = useMemo(() => {
    const filteredObjectives = (incidentObjectives ?? [])
      .filter((obj) => {
        if (obj.archived) return false;
        const type = obj.type || 'Operational';
        return type === 'Operational' || type === 'OperationalAndManagerial';
      })
      .slice()
      .sort(byNewest);

    return filteredObjectives.map((obj) => {
      const strategies = (incidentStrategies ?? [])
        .filter(
          (strat) =>
            !strat.archived && strat.objectives.includes(obj.objective_id)
        )
        .slice()
        .sort(byNewest)
        .map((strat) => {
          const tactics = (incidentTactics ?? [])
            .filter(
              (tac) =>
                !tac.archived && tac.strategies.includes(strat.strategy_id)
            )
            .slice()
            .sort(byNewest)
            .map((tac) => ({
              id: tac.tactic_id,
              name: tacticDisplayName(tac),
            }));
          return {
            id: strat.strategy_id,
            name: strat.name,
            tactics,
          };
        });

      return {
        id: obj.objective_id,
        name: obj.name,
        strategies,
      };
    });
  }, [incidentObjectives, incidentStrategies, incidentTactics]);

  const getCurrentName = (ref: Ics234MatrixItemRef): string => {
    if (ref.kind === 'objective') {
      return objectivesById.get(ref.objectiveId)?.name ?? '';
    }
    if (ref.kind === 'strategy') {
      return strategiesById.get(ref.strategyId)?.name ?? '';
    }
    const tactic = tacticsById.get(ref.tacticId);
    return tactic ? tacticDisplayName(tactic) : '';
  };

  const handleStartItemEdit = (ref: Ics234MatrixItemRef) => {
    setEditingItem({
      ref,
      draft: { kind: ref.kind, name: getCurrentName(ref) },
    });
  };

  const handleCancelItemEdit = () => setEditingItem(null);

  const handlePatchItemDraft = (draft: Ics234MatrixItemDraft) => {
    setEditingItem((prev) =>
      prev ? { ...prev, draft: { ...prev.draft, ...draft } } : prev
    );
  };

  const handleSaveItem = async () => {
    if (!editingItem) return;
    const { ref, draft } = editingItem;
    const trimmed = draft.name.trim();

    if (ref.kind === 'objective') {
      const item = objectivesById.get(ref.objectiveId);
      if (!item) return;
      await editObjective.mutateAsync({ ...item, name: trimmed });
    } else if (ref.kind === 'strategy') {
      const item = strategiesById.get(ref.strategyId);
      if (!item) return;
      await editStrategy.mutateAsync({ ...item, name: trimmed });
    } else {
      const item = tacticsById.get(ref.tacticId);
      if (!item) return;
      await editTactic.mutateAsync({ ...item, description: trimmed });
    }
    setEditingItem(null);
    recordVersion();
  };

  const handleAddObjective = async () => {
    const { data } = await createObjective.mutateAsync({
      name: 'New Objective',
      description: '',
      type: 'Operational',
    });
    recordVersion();
    const id = data?.id;
    if (!id) return;
    setEditingItem({
      ref: { kind: 'objective', objectiveId: id },
      draft: { kind: 'objective', name: data.name ?? 'New Objective' },
    });
  };

  const handleAddStrategy = async (objectiveId: string) => {
    const objective = objectivesById.get(objectiveId);
    const { data } = await createStrategy.mutateAsync({
      name: 'New Strategy',
      description: '',
      tactics: [],
      objectives: [objectiveId],
      incident_id: objective?.incident_id,
    });
    recordVersion();
    const created = data?.strategy ?? data;
    const id = created?.id;
    if (!id) return;
    setEditingItem({
      ref: { kind: 'strategy', objectiveId, strategyId: id },
      draft: { kind: 'strategy', name: created.name ?? 'New Strategy' },
    });
  };

  const handleAddTactic = async (objectiveId: string, strategyId: string) => {
    const strategy = strategiesById.get(strategyId);
    const { data } = await createTactic.mutateAsync({
      name: '',
      description: 'New Tactic',
      strategies: [strategyId],
      incident_id: strategy?.incident_id,
    });
    recordVersion();
    const created = data?.tactic ?? data;
    const id = created?.id;
    if (!id) return;
    const draftName = (
      created.description ||
      created.name ||
      'New Tactic'
    ).trim();
    setEditingItem({
      ref: { kind: 'tactic', objectiveId, strategyId, tacticId: id },
      draft: { kind: 'tactic', name: draftName },
    });
  };

  const handleDeleteObjective = async (objectiveId: string) => {
    const item = objectivesById.get(objectiveId);
    if (!item) return;
    const hasChildren = nestedObjectives.some(
      (obj) => obj.id === objectiveId && obj.strategies.length > 0
    );
    if (hasChildren) return;
    await editObjective.mutateAsync({ ...item, archived: true });
    recordVersion();
  };

  const handleDeleteStrategy = async (
    _objectiveId: string,
    strategyId: string
  ) => {
    const item = strategiesById.get(strategyId);
    if (!item) return;
    const hasChildren =
      incidentTactics?.some(
        (tac) => !tac.archived && tac.strategies.includes(strategyId)
      ) ?? false;
    if (hasChildren) return;
    await editStrategy.mutateAsync({ ...item, archived: true });
    recordVersion();
  };

  const handleDeleteTactic = async (
    _objectiveId: string,
    _strategyId: string,
    tacticId: string
  ) => {
    const item = tacticsById.get(tacticId);
    if (!item) return;
    await editTactic.mutateAsync({ ...item, archived: true });
    recordVersion();
  };

  const handleExport = (format: 'pdf' | 'docx' = 'pdf') => {
    void exportAsForm({
      formname: 'ICS_234_CG',
      data: {},
      mapSketch: null,
      format,
    });
  };

  const matrixProps = {
    objectives: nestedObjectives,
    canEdit,
    isSaving,
    editingItem,
    onStartItemEdit: handleStartItemEdit,
    onCancelItemEdit: handleCancelItemEdit,
    onPatchItemDraft: handlePatchItemDraft,
    onSaveItem: () => {
      void handleSaveItem();
    },
    onAddObjective: () => {
      void handleAddObjective();
    },
    onAddStrategy: (objectiveId: string) => {
      void handleAddStrategy(objectiveId);
    },
    onAddTactic: (objectiveId: string, strategyId: string) => {
      void handleAddTactic(objectiveId, strategyId);
    },
    onDeleteObjective: (objectiveId: string) => {
      void handleDeleteObjective(objectiveId);
    },
    onDeleteStrategy: (objectiveId: string, strategyId: string) => {
      void handleDeleteStrategy(objectiveId, strategyId);
    },
    onDeleteTactic: (
      objectiveId: string,
      strategyId: string,
      tacticId: string
    ) => {
      void handleDeleteTactic(objectiveId, strategyId, tacticId);
    },
  };

  const exportActions = (
    <FormExportButton
      formname="ICS_234_CG"
      onExport={handleExport}
      label="Preview ICS-234"
    />
  );

  const body = (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
      <FormVersioningBanner baseId="ICS_234" />

      <div className="tw:grid tw:gap-3 tw:md:grid-cols-3">
        <Ics204CardSection
          boxLabel={ICS234_BOX_LABELS.incidentName}
          testId="ics234-box-incident-name"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS234_BOX_LABELS.incidentName}>
              {ICS234_INCIDENT_NAME_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={incident.data?.name ?? ''}
            testId="ics234-incident-name"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS234_BOX_LABELS.incidentLocation}
          testId="ics234-box-incident-location"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS234_BOX_LABELS.incidentLocation}>
              {ICS234_INCIDENT_LOCATION_HINT}
            </BoxInfo>
          }
        >
          <PrintedValue
            value={
              incidentInfo?.incidentLocation || incident.data?.address || ''
            }
            testId="ics234-incident-location"
          />
        </Ics204CardSection>

        <Ics204CardSection
          boxLabel={ICS234_BOX_LABELS.operationalPeriod}
          testId="ics234-box-operational-period"
          className="tw:mt-0 tw:h-full tw:min-w-0"
          headerAction={
            <BoxInfo label={ICS234_BOX_LABELS.operationalPeriod}>
              {ICS234_OPERATIONAL_PERIOD_HINT}
            </BoxInfo>
          }
        >
          <OperationalPeriodScheduleControl testId="ics234-operational-period" />
        </Ics204CardSection>
      </div>

      <Ics204CardSection
        boxLabel={ICS234_BOX_LABELS.matrix}
        testId="ics234-box-matrix"
        className="tw:mt-0 tw:min-w-0"
        headerAction={
          <BoxInfo label={ICS234_BOX_LABELS.matrix}>
            {ICS234_MATRIX_HINT}
          </BoxInfo>
        }
      >
        <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
          <div className="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
            {canEdit ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={MATRIX_OUTLINE_BTN}
                onClick={() => {
                  void handleAddObjective();
                }}
              >
                + Add Objective
              </Button>
            ) : (
              <span />
            )}
            <div className="tw:flex tw:min-w-0 tw:flex-wrap tw:items-center tw:justify-end tw:gap-2">
              {matrixViewMode === 'table' ? (
                <span className="tw:text-xs tw:text-muted-foreground">
                  Use list view to reorder objectives.
                </span>
              ) : null}
              <ToggleGroup
                variant="outline"
                size="sm"
                spacing={0}
                multiple={false}
                value={[matrixViewMode]}
                onValueChange={(next) => {
                  const mode = next[0];
                  if (mode === 'list' || mode === 'table') {
                    setMatrixViewMode(mode);
                  }
                }}
                aria-label="Matrix view"
              >
                <ToggleGroupItem value="list" className="tw:gap-1 tw:px-2.5">
                  <LayoutList className="tw:h-3.5 tw:w-3.5" />
                  List view
                </ToggleGroupItem>
                <ToggleGroupItem value="table" className="tw:gap-1 tw:px-2.5">
                  <Table2 className="tw:h-3.5 tw:w-3.5" />
                  Table view
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          {matrixViewMode === 'table' ? (
            <Ics234WorkAnalysisMatrixTable {...matrixProps} />
          ) : (
            <Ics234WorkAnalysisMatrixList {...matrixProps} />
          )}
        </div>
      </Ics204CardSection>

      <Ics234SignatureBox periodNumber={effectivePeriodNumber} />
    </div>
  );

  // Beside the map on a future period there is no room for card chrome, so the
  // boxes stand on the panel itself, as the 202 does.
  if (isMapVisible && isFuture) {
    return (
      <div
        className="dark content tw:text-foreground mt-2"
        data-tour="ics234-card"
      >
        {body}
      </div>
    );
  }

  return (
    <Card
      data-testid="uscg-234-card"
      data-tour="ics234-card"
      data-form-family="uscg"
      className="dark tw:bg-background tw:text-foreground"
    >
      <CardHeader>
        <CardTitle>Work Analysis Matrix</CardTitle>
        <CardAction>{exportActions}</CardAction>
      </CardHeader>
      <CardContent>{body}</CardContent>
    </Card>
  );
};

export default Ics234StandaloneMatrix;
